import React, { type ReactNode } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';

import { canScopeRequestCredits, canScopeShowResetDate } from '../../common/credits-backend/scope';
import type { CreditDenialScope } from '../../common/credits-backend/types';
import type { CreditsBannerState } from '../../common/credits-ui-state/types';
import { RovoCreditsIntlProvider } from '../../i18n';
import { useCreditsRequestContext } from '../../provider/credits-request-context';
import { useCreditsStateContext } from '../../provider/credits-state-context';
import type { CreditsBannerProps } from '../../ui/credits-banner';
import { formatResetDate } from '../credits-banner-content/format-reset-date';
import { messages } from '../credits-banner-content/messages';
import { getDepletedDescriptor, normalizeAgentName } from '../credits-banner-content/scope';
import { RequestCreditsAction } from '../request-credits-action';
import { surfaceMessages } from './messages';
import {
	useCreditsSurfaceVisibility,
	type CreditsSurfaceVisibility,
} from './use-credits-surface-visibility';

const styles = cssMap({
	requestLink: {
		textDecorationLine: 'underline',
	},
});

type SurfaceContent = {
	title: string;
	formattedResetDate?: string;
	canRequestCredits: boolean;
	scope?: CreditDenialScope;
	agentName?: string;
	isWarning: boolean;
	dismiss: () => void;
	description: ReactNode;
	details: ReactNode;
};

type CreditsSurfaceProps = Omit<CreditsBannerProps, 'xcss'> & {
	children: (content: SurfaceContent) => ReactNode;
};

type CreditsSurfaceContentProps = Pick<
	CreditsSurfaceProps,
	'resetDate' | 'agentName' | 'children'
> &
	Omit<CreditsSurfaceVisibility, 'isVisible'> & { state: CreditsBannerState };

/** The copy of a visible credits surface. Render it inside `RovoCreditsIntlProvider`. */
export const CreditsSurfaceContent = ({
	state,
	isWarning,
	dismiss,
	resetDate,
	agentName,
	children,
}: CreditsSurfaceContentProps): React.JSX.Element => {
	const intl = useIntl();
	const request = useCreditsRequestContext();
	const { formatMessage } = intl;
	const { scope } = state;
	const formattedResetDate = canScopeShowResetDate(scope)
		? formatResetDate(intl, resetDate ?? state.resetDate)
		: undefined;
	const canRequestCredits =
		state.canRequestCredits === true && canScopeRequestCredits(scope) && !request?.isUnavailable;
	const trimmedAgentName = normalizeAgentName(agentName);
	const description = (
		<Stack space="space.200" alignInline="start">
			<Stack>
				<Text>
					{formatMessage(
						isWarning
							? messages.warningWithoutResetDate
							: getDepletedDescriptor({
									scope,
									agentName: trimmedAgentName,
									hasContext: false,
									hasResetDate: false,
								}),
						{ agentName: trimmedAgentName },
					)}
				</Text>
				{formattedResetDate ? (
					<Text>{formatMessage(surfaceMessages.resetDate, { resetDate: formattedResetDate })}</Text>
				) : null}
			</Stack>
			{canRequestCredits ? (
				<RequestCreditsAction isUnderlined>
					<Box as="span" xcss={styles.requestLink}>
						{formatMessage(surfaceMessages.requestCredits)}
					</Box>
				</RequestCreditsAction>
			) : null}
		</Stack>
	);
	const details = (
		<Stack space="space.100">
			{description}
			{isWarning ? (
				<Button appearance="subtle" onClick={dismiss}>
					{formatMessage(messages.dismissBanner)}
				</Button>
			) : null}
		</Stack>
	);

	return (
		<>
			{children({
				title: formatMessage(surfaceMessages.title),
				formattedResetDate,
				canRequestCredits,
				scope,
				agentName: trimmedAgentName,
				isWarning,
				dismiss,
				description,
				details,
			})}
		</>
	);
};

const Surface = ({
	state,
	onDismiss,
	onError,
	...props
}: CreditsSurfaceProps & { state: CreditsBannerState }): React.JSX.Element | null => {
	const { isVisible, isWarning, dismiss } = useCreditsSurfaceVisibility(state, {
		onDismiss,
		onError,
	});
	if (!isVisible) {
		return null;
	}
	return <CreditsSurfaceContent {...props} state={state} isWarning={isWarning} dismiss={dismiss} />;
};

/** Shared provider state and copy for the alternative credits presentations. */
export const CreditsSurface = (props: CreditsSurfaceProps): React.JSX.Element | null => {
	const credits = useCreditsStateContext();
	if (credits?.bannerState == null) {
		return null;
	}
	return (
		<RovoCreditsIntlProvider>
			<Surface {...props} state={credits.bannerState} />
		</RovoCreditsIntlProvider>
	);
};
